import { availabilityCheckRange, calculateAvailableSlots, scheduleTimeZone } from "./availability.js";
import { fredrick, meetingTypes, mockBookings, mockBusyIntervals, personas } from "./mockData.js";
import { MockProviderAdapter } from "./providers.js";
/** A day to answer, unless the meeting itself arrives sooner. */
export const DEFAULT_CONFIRMATION_WINDOW_HOURS = 24;
/**
 * A request stops holding the slot at whichever comes first: the host's window
 * running out, or the meeting being close enough that an answer would arrive too
 * late to be useful. Without the second clamp a request for a slot in two hours
 * would sit pending until long after the time had passed.
 */
export function approvalExpiry(now, startsAt, windowHours) {
    const windowEnds = Date.parse(now) + windowHours * 3_600_000;
    const tooLateToAnswer = Date.parse(startsAt) - APPROVAL_LEAD_MINUTES * 60_000;
    return new Date(Math.min(windowEnds, tooLateToAnswer)).toISOString();
}
/** An answer arriving inside this of the start is no use to anyone. */
const APPROVAL_LEAD_MINUTES = 15;
export class MockBookingCatalog {
    async listBusyIntervals(_hostId, _range) {
        return mockBusyIntervals;
    }
    async listActiveBookings(_hostId, meetingTypeId, _range) {
        return mockBookings.filter((booking) => booking.meetingTypeId === meetingTypeId);
    }
    async findPublicMeeting(hostSlug, personaSlug, meetingSlug) {
        const persona = personas.find((item) => item.slug === personaSlug && item.publicState !== "inactive");
        const meeting = meetingTypes.find((item) => item.slug === meetingSlug && item.personaId === persona?.id && item.active);
        if (!persona || !meeting || hostSlug !== fredrick.slug) {
            return undefined;
        }
        return {
            hostId: fredrick.id,
            tenantId: fredrick.tenantId,
            personaId: persona.id,
            meetingTypeId: meeting.id,
            meetingTitle: meeting.title,
            meetingDescription: meeting.description,
            durationMinutes: meeting.durationMinutes,
            personaLocation: persona.locationPreference,
            meetingType: meeting,
            personaName: persona.name,
            requiresConfirmation: persona.requiresConfirmation === true,
            confirmationWindowHours: persona.confirmationWindowHours ?? DEFAULT_CONFIRMATION_WINDOW_HOURS
        };
    }
}
export function slotKey(hostId, meetingTypeId, interval) {
    return `${hostId}#${meetingTypeId}#${interval.start}#${interval.end}`;
}
export class InMemorySlotHoldStore {
    holdsBySlot = new Map();
    holdsByIdempotency = new Map();
    bookingsById = new Map();
    bookingsByIdempotency = new Map();
    async createHold(hold) {
        this.expireOldHolds(new Date().toISOString());
        const existingForKey = this.holdsByIdempotency.get(hold.idempotencyKey);
        if (existingForKey) {
            return "duplicate_idempotency";
        }
        const key = slotKey(hold.hostId, hold.meetingTypeId, hold.interval);
        if (this.holdsBySlot.has(key)) {
            return "slot_taken";
        }
        this.holdsBySlot.set(key, hold);
        this.holdsByIdempotency.set(hold.idempotencyKey, hold);
        return "created";
    }
    async confirmHold(holdId, booking) {
        const hold = [...this.holdsBySlot.values()].find((item) => item.id === holdId);
        if (!hold) {
            throw new Error("hold_not_found");
        }
        this.bookingsById.set(booking.id, booking);
        this.bookingsByIdempotency.set(booking.idempotencyKey, booking);
        this.holdsBySlot.delete(slotKey(hold.hostId, hold.meetingTypeId, hold.interval));
    }
    async releaseHold(holdId) {
        const hold = [...this.holdsBySlot.values()].find((item) => item.id === holdId);
        if (!hold) {
            return;
        }
        this.holdsBySlot.delete(slotKey(hold.hostId, hold.meetingTypeId, hold.interval));
        this.holdsByIdempotency.delete(hold.idempotencyKey);
    }
    async getBookingByIdempotency(idempotencyKey) {
        return this.bookingsByIdempotency.get(idempotencyKey);
    }
    expireOldHolds(now) {
        for (const hold of this.holdsBySlot.values()) {
            if (Date.parse(hold.expiresAt) <= Date.parse(now)) {
                this.holdsBySlot.delete(slotKey(hold.hostId, hold.meetingTypeId, hold.interval));
                this.holdsByIdempotency.delete(hold.idempotencyKey);
            }
        }
    }
}
export class BookingWorkflow {
    dependencies;
    constructor(dependencies) {
        this.dependencies = dependencies;
    }
    async createPublicBooking(request) {
        const errors = validateBookingRequest(request);
        if (errors.length > 0) {
            return { status: "validation_failed", errors };
        }
        const existing = await this.dependencies.holdStore.getBookingByIdempotency(request.idempotencyKey);
        if (existing) {
            return { status: "duplicate", booking: existing };
        }
        const context = await this.dependencies.catalog.findPublicMeeting(request.hostSlug, request.personaSlug, request.meetingSlug);
        if (!context) {
            return { status: "validation_failed", errors: ["meeting_type_not_found"] };
        }
        // Whole days, not the half hour being booked: asked only about the slot, a
        // provider clips its all-day entries down to the slot and they stop looking like
        // the markers they are, so the page offers a time the booking then refuses.
        const checkRange = availabilityCheckRange(request.interval, scheduleTimeZone(context.meetingType.availability));
        const busyIntervals = await this.dependencies.catalog.listBusyIntervals(context.hostId, checkRange);
        const activeBookings = await this.dependencies.catalog.listActiveBookings(context.hostId, context.meetingTypeId, checkRange);
        const availableSlots = calculateAvailableSlots({
            meetingType: context.meetingType,
            range: checkRange,
            busyIntervals,
            existingBookings: activeBookings,
            now: request.now
        });
        const requestedSlotIsAvailable = availableSlots.some((slot) => slot.start === request.interval.start && slot.end === request.interval.end);
        if (!requestedSlotIsAvailable) {
            return { status: "slot_taken" };
        }
        const holdId = `hold_${crypto.randomUUID()}`;
        const holdResult = await this.dependencies.holdStore.createHold({
            id: holdId,
            hostId: context.hostId,
            meetingTypeId: context.meetingTypeId,
            interval: request.interval,
            idempotencyKey: request.idempotencyKey,
            expiresAt: new Date(Date.parse(request.now) + 10 * 60_000).toISOString()
        });
        if (holdResult === "duplicate_idempotency") {
            const duplicate = await this.dependencies.holdStore.getBookingByIdempotency(request.idempotencyKey);
            return duplicate ? { status: "duplicate", booking: duplicate } : { status: "slot_taken" };
        }
        if (holdResult === "slot_taken") {
            return { status: "slot_taken" };
        }
        const booking = {
            id: `booking_${crypto.randomUUID()}`,
            tenantId: context.tenantId,
            hostId: context.hostId,
            personaId: context.personaId,
            meetingTypeId: context.meetingTypeId,
            guestName: request.guest.name.trim(),
            guestEmail: request.guest.email.trim().toLowerCase(),
            // The booking form asks for these, so keep them: they were being dropped,
            // which left the host with no record of why the guest booked.
            ...(request.guest.phone?.trim() ? { guestPhone: request.guest.phone.trim() } : {}),
            ...(request.guest.organization?.trim() ? { guestOrganization: request.guest.organization.trim() } : {}),
            ...(request.guest.reason?.trim() ? { guestReason: request.guest.reason.trim() } : {}),
            interval: request.interval,
            status: context.requiresConfirmation ? "pending_approval" : "confirmed",
            ...(context.requiresConfirmation
                ? { approvalExpiresAt: approvalExpiry(request.now, request.interval.start, context.confirmationWindowHours) }
                : {}),
            destinationProvider: "mock",
            idempotencyKey: request.idempotencyKey,
            createdAt: request.now,
            updatedAt: request.now
        };
        try {
            const providerResult = await this.dependencies.providerAdapter.createEvent({
                booking,
                // The host's own calendar says what this is at a glance, and for Zoho - which
                // has no tentative status - the title is the only signal there is.
                summary: context.requiresConfirmation
                    ? `Requested: ${context.meetingTitle} with ${booking.guestName}`
                    : `${context.meetingTitle} with ${booking.guestName}`,
                description: context.meetingDescription,
                location: context.personaLocation,
                destinationCalendarId: "mock-destination",
                ...(context.requiresConfirmation ? { tentative: true } : {})
            });
            const confirmed = {
                ...booking,
                destinationProvider: providerResult.provider,
                ...(providerResult.destinationCalendarId ? { destinationCalendarId: providerResult.destinationCalendarId } : {}),
                ...(providerResult.joinUrl ? { joinUrl: providerResult.joinUrl } : {}),
                externalEventId: providerResult.externalEventId
            };
            await this.dependencies.holdStore.confirmHold(holdId, confirmed);
            return {
                status: context.requiresConfirmation ? "pending_approval" : "confirmed",
                booking: confirmed,
                providerSentInvite: providerResult.providerSentInvite,
                ...(providerResult.joinUrl ? { joinUrl: providerResult.joinUrl } : {})
            };
        }
        catch (cause) {
            await this.dependencies.holdStore.releaseHold(holdId);
            throw cause;
        }
    }
}
export function createMockBookingWorkflow(holdStore = new InMemorySlotHoldStore()) {
    return new BookingWorkflow({
        catalog: new MockBookingCatalog(),
        holdStore,
        providerAdapter: new MockProviderAdapter(mockBusyIntervals)
    });
}
function validateBookingRequest(request) {
    const errors = [];
    if (!request.idempotencyKey || request.idempotencyKey.length < 12) {
        errors.push("idempotency_key_required");
    }
    if (!request.guest.name.trim()) {
        errors.push("guest_name_required");
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(request.guest.email.trim())) {
        errors.push("valid_guest_email_required");
    }
    if (!request.guest.consentAccepted) {
        errors.push("consent_required");
    }
    if (Date.parse(request.interval.end) <= Date.parse(request.interval.start)) {
        errors.push("invalid_interval");
    }
    return errors;
}
